import React from 'react';
import { Check } from 'lucide-react';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { useT } from '../../salesI18n';
import { priceTypeFor } from '../../../components/BoxDrawLayer';
import { OptionIcon } from '../../../components/options/OptionIcon';
import { ruleFilter as rulesFilter } from '../../../components/options/optionCategories';

export const DEFAULT = '__default__';
const KEEP = 'keep';
const PILL_ON = 'border-teal-600 bg-teal-50 text-teal-800 ring-2 ring-teal-500/25';
const PILL_OFF = 'border-slate-200 bg-white/70 text-slate-600 hover:bg-white';

export const LOW_E_TYPES = ['none', 'climatech', 'climatech_elite', 'climatech_sf', 'argon'];
// Opções que a linha do produto (Mezzo/Fusion) fabrica — palette.line_options (união das tabelas da linha) ou, com a abertura, a tabela exata (palette.table_options["linha"]["TIPO" | "TIPO:folhas"])
export const lineFilter = (palette, line, opening) => {
  const lo = (opening && tableOptionsFor(palette, line, opening)) || palette?.line_options?.[line || 'mezzo'];
  return (kind, all) => (lo?.[kind] ? all.filter((x) => lo[kind].includes(x)) : all);
};
const tableOptionsFor = (palette, line, { type, style, units }) => {
  const byLine = palette?.table_options?.[line || 'mezzo'];
  if (!byLine) return null;
  const effType = priceTypeFor(type, style, palette.style_to_type);
  const n = Math.min(5, Number(units) || 1);
  return (style && byLine[`${effType}@${style}`]) || byLine[`${effType}:${n}`] || byLine[effType] || null;  // tabela do modelo (awning variantes) > nº de folhas > genérica
};
// Opções que o Admin liberou p/ a linha × tipo efetivo da abertura (palette.option_rules[linha] = {base, by_type}); sem regra → tudo
export const ruleFilter = (palette, line, opening) => {
  const rules = palette?.option_rules?.[line || ''];
  const type = opening?.type ? priceTypeFor(opening.type, opening.style, palette?.style_to_type) : null;
  return rulesFilter(rules, type);
};
export const allowedColors = (palette, line, opening, kind) => ruleFilter(palette, line, opening)(`${kind}_colors`, (palette?.[kind] || []).map((c) => c.id));
// Linhas (Mezzo/Fusion) que têm tabela de fábrica p/ o tipo efetivo da abertura; sem tabela em nenhuma linha (legado) → todas
export const linesForOpening = (palette, opening, style) => {
  const all = palette?.product_lines || ['mezzo', 'fusion'];
  if (!opening?.type || !palette?.table_options) return all;
  const effType = priceTypeFor(opening.type, style, palette.style_to_type);
  const has = (line) => Object.keys(palette.table_options[line] || {}).some((k) => k === effType || k.startsWith(`${effType}:`) || k.startsWith(`${effType}@`));
  const lines = all.filter(has);
  return lines.length ? lines : all;
};
export const TINT_TYPES = ['none', 'bronze', 'gray'];
export const COVERAGES = ['full', 'top', 'ends', 'center'];

// Select simples (vidro / tela). allowDefault → opção "Padrão da proposta" (value null)
export const SpecSelect = ({ label, options, labels, value, onChange, allowDefault = false, defaultValue, defaultLabel, testId, hint, warn = false, kind }) => {
  const { t } = useT();
  const item = (o) => (kind ? <span className="inline-flex items-center gap-2"><OptionIcon kind={kind} value={o} size={22} /> {labels[o]}</span> : labels[o]);
  return (
    <div>
      <Label className="text-[11px] font-semibold text-slate-600">{label}</Label>
      <Select value={value ?? (allowDefault ? DEFAULT : options[0])} onValueChange={(v) => onChange(v === DEFAULT ? null : v)}>
        <SelectTrigger className={`h-9 text-xs mt-1 ${warn ? 'border-amber-400 bg-amber-50/60' : ''}`} data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>
          {allowDefault && <SelectItem value={DEFAULT}>{defaultLabel || t.useProposalDefault} ({labels[defaultValue || options[0]]})</SelectItem>}
          {options.map((o) => <SelectItem key={o} value={o} data-testid={`${testId}-opt-${o}`}>{item(o)}</SelectItem>)}
        </SelectContent>
      </Select>
      {hint && <p className={`text-[10px] mt-0.5 ${warn ? 'text-amber-700 font-semibold' : 'text-slate-500'}`} data-testid={warn ? `${testId}-warn` : undefined}>{hint}</p>}
    </div>
  );
};

// Acabamento (trim). value: null/undefined = (allowDefault ? padrão : manter) · {style:'keep'} = manter · {style, color_id} = aplicar
export const TrimPicker = ({ kind, value, onChange, palette, allowDefault = false, defaultValue, defaultLabel, testId, line, opening }) => {
  const { t } = useT();
  const byRule = ruleFilter(palette, line, opening);
  const styles = byRule(`${kind}_trims`, kind === 'exterior' ? palette.exterior_trim_styles || [] : palette.interior_trim_styles || []);
  const names = kind === 'exterior' ? t.extTrimStyles : t.intTrimStyles;
  const colorIds = allowedColors(palette, line, opening, kind);
  const colors = (palette[kind] || []).filter((c) => colorIds.includes(c.id));
  const sel = value == null ? (allowDefault ? DEFAULT : KEEP) : value.style;
  const describe = (v) => (!v || !v.style || v.style === KEEP ? t.trimKeep : names[v.style]);
  return (
    <div data-testid={testId}>
      <Label className="text-[11px] font-semibold text-slate-600">{kind === 'exterior' ? t.exteriorTrim : t.interiorTrim}</Label>
      <Select value={sel} onValueChange={(v) => onChange(v === DEFAULT ? null : v === KEEP ? (allowDefault ? { style: KEEP } : null) : { style: v, color_id: kind === 'interior' ? null : value?.color_id || null })}>
        <SelectTrigger className="h-9 text-xs mt-1" data-testid={`${testId}-style`}><SelectValue /></SelectTrigger>
        <SelectContent>
          {allowDefault && <SelectItem value={DEFAULT}>{defaultLabel || t.useProposalDefault} ({describe(defaultValue)})</SelectItem>}
          <SelectItem value={KEEP} data-testid={`${testId}-opt-keep`}>{t.trimKeep}</SelectItem>
          {styles.map((s) => <SelectItem key={s} value={s} data-testid={`${testId}-opt-${s}`}><span className="inline-flex items-center gap-2"><OptionIcon kind={kind === 'exterior' ? 'ext_trim' : 'int_trim'} value={s} size={22} /> {names[s]}</span></SelectItem>)}
        </SelectContent>
      </Select>
      {value?.style && value.style !== KEEP && kind === 'interior' ? <p className="text-[10px] text-slate-500 mt-0.5" data-testid={`${testId}-primed`}>{t.intTrimPrimed}</p> : value?.style && value.style !== KEEP ? (
        <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`${testId}-colors`}>
          <button type="button" onClick={() => onChange({ ...value, color_id: null })} aria-pressed={!value.color_id} className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-full border text-[11px] font-semibold ${!value.color_id ? PILL_ON : PILL_OFF}`} data-testid={`${testId}-color-same`}>{!value.color_id && <Check className="h-3 w-3" />} {t.trimSameAsWindow}</button>
          {colors.map((c) => (
            <button key={c.id} type="button" onClick={() => onChange({ ...value, color_id: c.id })} aria-pressed={value.color_id === c.id} className={`inline-flex items-center gap-1 h-7 pl-1 pr-2.5 rounded-full border text-[11px] font-semibold ${value.color_id === c.id ? PILL_ON : PILL_OFF}`} data-testid={`${testId}-color-${c.id}`}>
              <span className="h-4 w-4 rounded-full border border-black/10" style={{ background: c.hex }} /> {c.name}
            </button>
          ))}
        </div>
      ) : sel === KEEP ? <p className="text-[10px] text-slate-500 mt-0.5">{t.trimKeepHint}</p> : null}
    </div>
  );
};

// Rótulo do modelo: janela (t.styles) ou porta (t.doorStyles; padrão da proposta não se aplica → Contemporary)
export const DOOR_TYPE_IDS = ['PATIO_DOOR', 'ENTRY_DOOR'];
export const styleLabel = (t, style, type) => (DOOR_TYPE_IDS.includes(type) ? t.doorStyles[style && style !== 'keep' && t.doorStyles[style] ? style : (type === 'PATIO_DOOR' ? 'patio_sliding' : 'entry_full_lite')] : t.styles[style] || style);

// Texto curto do acabamento/vidro/tela p/ resumos
export const trimText = (t, kind, v) => (!v || !v.style || v.style === 'keep' ? t.trimKeep : `${(kind === 'exterior' ? t.extTrimStyles : t.intTrimStyles)[v.style] || v.style}${v.color?.name ? ` ${v.color.name}` : ''}`);
export const specChips = (t, o) => [
  o.door_model ? (String(o.door_model).startsWith('Custom') ? t.customDoor(o.units || o.door_panels || 2) : t.doorModelLabel(o.door_model)) : o.door_panels ? t.customDoor(o.door_panels) : null,
  o.foot_lock ? t.footLock : null,
  o.key_lock ? t.keyLock : null,
  o.grille && o.grille !== 'none' && o.grille_coverage && o.grille_coverage !== 'full' ? `${t.grilleCoverage}: ${t.coverages[o.grille_coverage]}` : null,
  o.low_e && o.low_e !== 'none' ? `${t.lowE} ${t.lowETypes[o.low_e]}` : null,
  o.tint && o.tint !== 'none' ? `${t.tint.replace(/ \(.*\)$/, '')} ${t.tintTypes[o.tint]}` : null,
  o.glass_type && o.glass_type !== 'clear' ? t.glassTypes[o.glass_type] : null,
  o.screen && o.screen !== 'none' ? t.screens[o.screen] : null,
  o.exterior_trim?.style && o.exterior_trim.style !== 'keep' ? `${t.exteriorTrim}: ${trimText(t, 'exterior', o.exterior_trim)}` : null,
  o.interior_trim?.style && o.interior_trim.style !== 'keep' ? `${t.interiorTrim}: ${trimText(t, 'interior', o.interior_trim)}` : null,
].filter(Boolean);
